'use client';

import useSWR from 'swr';
import { getGeoJsonBulkFromCache, setGeoJsonBulkToCache } from '@/shared/lib/cache';
import { getServiceRegionsByIds } from '@/entities/order/model/service-region.client';
import { getLegalRegionsByIds } from './legal-region.client';

/**
 * serviceRegionIds → legalRegionIds 수집 → districtCode 추출 → Firestore bulk 조회(100건씩 청크) → 병합
 *
 * @param serviceRegionIds ServiceRegion ID 배열
 * @returns 병합된 GeoJSON FeatureCollection (legalRegionId별로 중복 제거 후 합침)
 */
async function fetchGeoJsonByServiceRegionIds(
  serviceRegionIds: string[]
): Promise<GeoJSON.FeatureCollection> {
  if (serviceRegionIds.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  const serviceRegions = await getServiceRegionsByIds(serviceRegionIds);
  const legalRegionIds = new Set<string>();
  for (const sr of serviceRegions) {
    if (sr?.legalRegions?.length) {
      sr.legalRegions.forEach((id) => legalRegionIds.add(id));
    }
  }

  const legalRegionMap = await getLegalRegionsByIds(Array.from(legalRegionIds));

  const districtCodeToLegalRegionId = new Map<string, string>();
  const districtCodes: string[] = [];
  for (const legalRegionId of legalRegionIds) {
    const legalRegion = legalRegionMap.get(legalRegionId) ?? null;
    const districtCode = legalRegion?.district?.code ?? legalRegion?.code ?? legalRegionId;
    districtCodeToLegalRegionId.set(districtCode, legalRegionId);
    districtCodes.push(districtCode);
  }

  const uniqueCodes = Array.from(new Set(districtCodes));
  if (uniqueCodes.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  // 캐시 우선 조회, 미캐시된 코드만 bulk API 호출
  const [cachedMap, missedCodes] = await getGeoJsonBulkFromCache(uniqueCodes);
  const geomMap: Record<string, string> = Object.fromEntries(cachedMap);

  if (missedCodes.length > 0) {
    const BULK_CHUNK_SIZE = 100;
    const toCache: Record<string, string> = {};

    for (let i = 0; i < missedCodes.length; i += BULK_CHUNK_SIZE) {
      const chunk = missedCodes.slice(i, i + BULK_CHUNK_SIZE);
      const res = await fetch('/api/geometry/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ districtCodes: chunk }),
        cache: 'no-store',
      });
      if (!res.ok) continue;
      const { geometries } = (await res.json()) as { geometries?: Record<string, string> };
      if (geometries) {
        for (const [code, geometryStr] of Object.entries(geometries)) {
          if (geometryStr) {
            geomMap[code] = geometryStr;
            toCache[code] = geometryStr;
          }
        }
      }
    }

    if (Object.keys(toCache).length > 0) {
      await setGeoJsonBulkToCache(toCache);
    }
  }

  const features: GeoJSON.Feature[] = [];
  for (const [districtCode, geometryStr] of Object.entries(geomMap)) {
    if (!geometryStr) continue;
    const legalRegionId = districtCodeToLegalRegionId.get(districtCode) ?? districtCode;
    try {
      const parsed = JSON.parse(geometryStr) as GeoJSON.GeoJSON;
      if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
        features.push(...parsed.features);
      } else if (parsed.type === 'Feature') {
        features.push(parsed);
      } else if (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon') {
        features.push({
          type: 'Feature',
          geometry: parsed,
          properties: { legalRegionId },
        });
      }
    } catch {
      // ignore parse error
    }
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

export function useGeoJsonByServiceRegionIds(
  serviceRegionIds: string[],
  enabled = true
): {
  geoJson: GeoJSON.FeatureCollection;
  isLoading: boolean;
  error: Error | null;
} {
  const stableIds = [...serviceRegionIds].sort().join(',');
  const swrKey =
    enabled && serviceRegionIds.length > 0 ? ['geo-json-by-service-regions', stableIds] : null;

  const { data, error, isLoading } = useSWR<GeoJSON.FeatureCollection>(
    swrKey,
    () => fetchGeoJsonByServiceRegionIds(serviceRegionIds),
    {
      revalidateOnMount: true,
      revalidateIfStale: true,
      dedupingInterval: 5000,
    }
  );

  return {
    geoJson: data ?? { type: 'FeatureCollection', features: [] },
    isLoading,
    error: error ? (error instanceof Error ? error : new Error(String(error))) : null,
  };
}
